import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Button, FormControl, Grid } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { SyntheticEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUnidadeSelecionada } from "../../../../features/usuario-logado/usuarioLogadoSlice";
import ErrorBadge from "../../../common/components/forms/errorBadge";
import FormHeaderMedium from "../../../common/components/forms/formHeaderMedium";
import LoadingIndicator from "../../../common/components/loadingIndicator";
import { useUnidadeNegocioGuard } from "../../../common/hooks/useUnidadeNegocioGuard";
import PageLayout from "../../../common/layouts/pageLayout";
import { FollowUpDto } from "../../../common/types/followUp";
import { KEYCLOAK_TOKEN_TIMEOUT } from "../../../../App";
import { setErrorFeedback, setSuccessFeedback } from "../../../../features/feedback/feedbackSlice";
import { fetchByFilterDto } from "../../../../features/follow-up/followUpAPI";
import { downloadXlsxFile, fetchReportXlsx } from "./martinsLog.hooks"; 
import MultiSelectStyled, { SimpleMultiSelectOption } from "../../../common/components/input/multiSelectStyled";
import QCXFinalDatePickerField from "../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField";
import { Field, Form } from "react-final-form";
import { fetchClientesWithFilterDto } from "../../../../features/cliente/clienteAPI";

const MartinsLogImportPage = () => {
  useUnidadeNegocioGuard();
  const dispatch = useDispatch();

  const [error, setError] = useState<string | string[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const [selectedProcessos, setSelectedProcessos] = useState<SimpleMultiSelectOption[]>([]);
  const [dataInicio, setDataInicio] = useState<string | null>(null);
  const [dataFim, setDataFim] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);

  const [clientes, setClientes] = useState<any[]>([]);
  const [processosList, setProcessosList] = useState<string[]>([]);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState<boolean>(false);

  const { keycloak } = useKeycloak();
  const selectedUnidade: number = useSelector(selectUnidadeSelecionada);
  const { token } = keycloak;

  const url = `${process.env.REACT_APP_QCX_API_BASE_URL!}/${process.env.REACT_APP_QCX_API_FATURA_ENDPOINT!}/martins/import-report`;

  const clearData = () => {
    setSelectedProcessos([]);
    setDataInicio(null);
    setDataFim(null);
    setSelectedClients([]);
    setIsLoading(false);
  };

  const handleClientsChange = (event: SyntheticEvent<Element, Event>, value: SimpleMultiSelectOption[] | null) => {
    setSelectedClients(value || []);
    setSelectedProcessos([]);
  };

  const hasAnyFilter = selectedClients.length > 0 || selectedProcessos.length > 0 || !!dataInicio || !!dataFim;

  const handleSubmit = async () => {
    if (hasAnyFilter && token && selectedUnidade !== undefined) {
      keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);
      setIsLoading(true);

      let inicioFormatado = "";
      let fimFormatado = "";

      try {
        if (dataInicio) inicioFormatado = new Date(dataInicio).toISOString().slice(0, 10);
        if (dataFim) fimFormatado = new Date(dataFim).toISOString().slice(0, 10);
      } catch (e) {
        dispatch(setErrorFeedback({ message: "Formato de data inválido." }));
        setIsLoading(false);
        return;
      }

      const clientesStr = selectedClients.map(c => c.id).join(',');
      const processosStr = selectedProcessos.map(p => p.id).join(',');

      const queryParams = new URLSearchParams();
      if (processosStr) queryParams.append("processos", processosStr);
      if (inicioFormatado) queryParams.append("inicio", inicioFormatado);
      if (fimFormatado) queryParams.append("fim", fimFormatado);
      if (clientesStr) queryParams.append("clientes", clientesStr);

      const endpoint = `${url}/gerar-xlsx?${queryParams.toString()}`;
      const data = await fetchReportXlsx(token, selectedUnidade.toString(), endpoint);

      if (data.statusCode === 500 || data.statusCode === 400 || data.statusCode === 404) {
        dispatch(setErrorFeedback({
          message: data.message || "Falha ao gerar relatório Broker File."
        }));
      } else if (data.statusCode === 200 && data.file) {
        const dataAtual = new Date().toISOString().slice(0, 10);
        const fileName = `Broker File - Martins - ${dataAtual}.xlsx`;
        
        downloadXlsxFile(data.file, fileName);

        dispatch(setSuccessFeedback({
          message: "Broker File gerado com sucesso!"
        }));

        clearData();
      }

      setIsLoading(false);
    }
  };

  const fetchDadosIniciais = async () => {
    if (!selectedUnidade) return;

    try {
      const { data } = await fetchClientesWithFilterDto([
        { name: 'filterDadosMinimos', value: 'TRUE' },
        { name: 'unidadesDeNegocio.id', value: selectedUnidade.toString() }
      ]);
      setClientes(data);
    } catch (error) {
      dispatch(setErrorFeedback({ message: 'Erro ao obter clientes:' }));
      setClientes([]);
    }
  };

  useEffect(() => {
    const fetchProcessosPorCliente = async () => {
      if (selectedClients.length > 0) {
        setIsFetchingProcesses(true);
        try {
          const clientIds = selectedClients.map(c => c.id).join(',');

          const { data } = await fetchByFilterDto([
            { name: 'filterDadosMinimos', value: 'TRUE' },
            { name: 'tennantUnidadeDeNegocio.id', value: selectedUnidade.toString() },
            { name: 'importador.id', value: clientIds } 
          ]);

          const mapeados = data
            .map((followUp: FollowUpDto) => followUp.numero)
            .filter((numero: any) => numero != null && numero.trim() !== '');

          const unicos = mapeados.filter((proc: string, index: number) => mapeados.indexOf(proc) === index);

          setProcessosList(unicos);
        } catch (error) {
          dispatch(setErrorFeedback({ message: 'Erro ao carregar processos dos clientes.' }));
          setProcessosList([]);
        } finally {
          setIsFetchingProcesses(false);
        }
      } else {
        setProcessosList([]);
        setSelectedProcessos([]);
      }
    };

    fetchProcessosPorCliente();
  }, [selectedClients, selectedUnidade]);

  useEffect(() => {
    fetchDadosIniciais();
    setSelectedClients([]);
    setSelectedProcessos([]);
  }, [selectedUnidade]);

  return (
    <PageLayout title={"Broker File"} icon={<InsertDriveFileIcon color={"secondary"} />}>
      {isLoading ? (
        <LoadingIndicator message={isLoading ? "Gerando Broker File..." : "Carregando Dados..."} />
      ) : (
        <Form onSubmit={() => { }}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <FormHeaderMedium>
                  Selecione as informações para gerar o arquivo
                </FormHeaderMedium>

                <Grid item sm={12} style={{ margin: '10px 0 0 0' }}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      disablePortal={false}
                      controlledValues={selectedClients}
                      options={(clientes || []).map((client: any) => ({
                        id: client.id ?? '',
                        value: `(${client.pessoa?.cnpj || ''}) ${client.pessoa?.nome || ''}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                <Grid item sm={6} md={5} lg={6} style={{ marginTop: '10px' }}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      options={processosList.map((proc) => ({
                        id: proc,
                        value: proc,
                      }))}
                      controlledValues={selectedProcessos}
                      onChangeAction={(e, val) => setSelectedProcessos(val || [])}
                      label={isFetchingProcesses ? "Carregando Processos..." : "Processos"}
                      disabled={selectedClients.length === 0 || isFetchingProcesses}
                      placeholder={selectedClients.length === 0 ? "Selecione um Cliente primeiro" : "Selecionar Processos"}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} md={3} style={{ marginTop: '10px' }}>
                  <Field name="dataInicioRegistro" render={({ input }) => (
                    <QCXFinalDatePickerField
                      required={false}
                      validate={undefined}
                      id="date-picker-inicio"
                      key="date-picker-inicio"
                      label="Data Início Registro"
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      {...input}
                      onChange={(date: string | null) => {
                        setDataInicio(date);
                        input.onChange(date);
                      }}
                    />
                  )} />
                </Grid>

                <Grid item xs={12} sm={4} md={3} style={{ marginTop: '10px' }}>
                  <Field name="dataFimRegistro" render={({ input }) => (
                    <QCXFinalDatePickerField
                      required={false}
                      validate={undefined}
                      id="date-picker-fim"
                      key="date-picker-fim"
                      label="Data Fim Registro"
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      {...input}
                      onChange={(date: string | null) => {
                        setDataFim(date);
                        input.onChange(date);
                      }}
                    />
                  )} />
                </Grid>

                <ErrorBadge error={error} />

                <Grid item sm={12} textAlign={"right"}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSubmit}
                    disabled={!hasAnyFilter || isLoading || !!error}
                  >
                    Gerar
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      )}
    </PageLayout>
  );
};

export default MartinsLogImportPage;